<template>
  <div :style="styling"  >
    <label v-if="label">{{ label }}</label>
    <div class="input_text_field">
      <input
        ref="field"
        :style="inputStyling"
        :type="passwordType"
        autocomplete="off"
        data-lpignore="true"
        :disabled="disabled"
        :value="modelValue || value"
        :placeholder="placeholder"
        :min="min"
        :max="max"
        onkeyup="this.setAttribute('value', this.value);"
        @focus="onFocus"
        @blur="onBlur"
        @input="update"
      >
      <div v-if="password == ''" class="box center" :style="boxStyling" @click="showPassword = !showPassword">
        <s-icon v-show="!showPassword && !disabled" :title="tip && t('atoms.tip.show_password')" width="24" color="primary">
          eye-outline
        </s-icon>
        <s-icon v-show="showPassword && !disabled" :title="tip && t('atoms.tip.hide_password')" width="24" color="primary">
          eye-off-outline
        </s-icon>
      </div>
    </div>
    <span v-if="details"  class="errorMessage" >{{ error }}</span>
    <div v-if="bar !== undefined" class="d-flex strength_container">
      <div v-show="strength > 0" class="strength red" />
      <div v-show="strength > 1" class="strength orange" />
      <div v-show="strength > 2" class="strength green" />
      <div v-show="strength > 3" class="strength blue" />
    </div>
    <div v-if="bar !== undefined && passwordRequirements.length > 0" class="password_requirements">
      <div class="requirements_title">Password must include:</div>
      <div class="requirements_list">
        <div 
          v-for="requirement in passwordRequirements" 
          :key="requirement.key"
          class="requirement_item"
          :class="{ 'requirement_met': requirement.met }"
        >
          <s-icon 
            :color="requirement.met ? 'success' : 'error'"
            width="16"
            class="requirement_icon"
          >
            {{ requirement.met ? 'checkmark-circle' : 'close-circle' }}
          </s-icon>
          {{ requirement.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-bootstrap.css';

const store = useStore();
const { t } = useI18n();
const $toast = useToast();
const emit = defineEmits()

const props = defineProps([
  "value",
  "error",
  "label",
  "modelValue",
  "type",
  "details",
  "block",
  "placeholder",
  "height",
  "disabled",
  "textCenter",
  "bar",
  "password",
  "noedit",
  "autofocus",
  "width",
  "min",
  "max",
]);

const strength = ref(0);
const field = ref(0);
const showPassword = ref(false);

const tip = computed(() => store.state.user.tip);

const onFocus = () => {
  if (!field.value) return;
  if (props.autofocus == "") field.value.focus()
  emit("focus");
};

const parseBound = (b) => {
  if (b === undefined || b === null || b === "") return undefined;
  const n = Number(b);
  return isNaN(n) ? undefined : n;
};

const onBlur = () => {
  if (props.type === "number" && field.value) {
    const lo = parseBound(props.min);
    const hi = parseBound(props.max);
    if (lo !== undefined || hi !== undefined) {
      const raw = field.value.value;
      const trimmed = raw.trim();
      if (trimmed === "") {
        field.value.value = "";
        emit("update:modelValue", "");
        emit("blur");
        return;
      }
      const num = Number(trimmed);
      if (isNaN(num)) {
        $toast.warning("Enter a valid number", {
          position: "top-right",
          duration: 2500,
        });
        emit("update:modelValue", trimmed);
        nextTick(() => field.value?.focus());
        emit("blur");
        return;
      }
      const outOfRange =
        (lo !== undefined && num < lo) ||
        (hi !== undefined && num > hi);
      if (outOfRange) {
        let message = "Value is out of allowed range";
        if (lo !== undefined && hi !== undefined) {
          message = `Value must be between ${lo} and ${hi}`;
        } else if (lo !== undefined) {
          message = `Value must be at least ${lo}`;
        } else if (hi !== undefined) {
          message = `Value must be at most ${hi}`;
        }
        $toast.warning(message, {
          position: "top-right",
          duration: 2500,
        });
        emit("update:modelValue", trimmed);
        nextTick(() => field.value?.focus());
        emit("blur");
        return;
      }
      const s = String(num);
      if (field.value.value !== s) {
        field.value.value = s;
      }
      emit("update:modelValue", s);
    }
  }
  emit("blur");
};

const update = () => {
  const value = field.value ? field.value.value : "";
  emit("update:modelValue", value);
};

const passwordStrength = () => {
  strength.value = 0;
  if (props.bar === undefined) return;
  
  const currentValue = props.modelValue || props.value || '';
  if (currentValue.length === 0) return strength.value = 0;
  
  if (/[a-z]/.test(currentValue)) strength.value++;
  
  if (/[A-Z]/.test(currentValue)) strength.value++;
  
  if (/[0-9]/.test(currentValue)) strength.value++;
  
  if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(currentValue)) strength.value++;
  
  if (currentValue.length > 12) strength.value++;
  
  if (currentValue.length < 8) strength.value = Math.min(strength.value, 1);
};

const styling = computed(() => {
  let ob = {};
  if (props.block == "" || props.block == "true") ob.width = "100%";
  if (props.details == "" || props.details == "true") ob.minHeight = "55px";

  if (props.label && props.label.length > 0) ob.paddingTop = "0px";
  return ob;
});

const boxStyling = computed(() => {
  let ob = {};
  if (props.height) ob.height = props.height + "px";
  if (props.height) ob.minWidth = props.height + "px";
  if (props.height) ob.maxWidth = props.height + "px";
  return ob;
});

const inputStyling = computed(() => {
  let ob = {};
  if (props.textCenter == "" || props.textCenter == "true") ob.textAlign = "center";
  if (props.height) ob.height = props.height + "px";
  if (props.width) ob.width = props.width + "px";
  if (props.noedit == "") ob.backgroundColor = "#00000000";
  return ob;
});

const passwordType = computed(() => {
  let show = showPassword.value ? "text" : "password";
  return props.password != "" ? props.type : show;
});

const passwordRequirements = computed(() => {
  if (props.bar === undefined) return [];
  
  const currentValue = props.modelValue || props.value || '';
  const requirements = [];
  
  requirements.push({
    key: 'length',
    text: 'At least 8 characters',
    met: currentValue.length >= 8
  });
  
  requirements.push({
    key: 'lowercase',
    text: 'One lowercase letter (a-z)',
    met: /[a-z]/.test(currentValue)
  });
  
  requirements.push({
    key: 'uppercase',
    text: 'One uppercase letter (A-Z)',
    met: /[A-Z]/.test(currentValue)
  });
  
  requirements.push({
    key: 'number',
    text: 'One number (0-9)',
    met: /[0-9]/.test(currentValue)
  });
  
  requirements.push({
    key: 'special',
    text: 'One special character (!@#$%^&*...)',
    met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(currentValue)
  });
  
  return currentValue.length === 0 ? requirements : requirements.filter(req => !req.met);
});

watch(() => props.value, (val) => {
  passwordStrength();
});

watch(() => props.modelValue, (val) => {
  passwordStrength();
});

onMounted(() => {
  if (props.autofocus == "") onFocus();
});

defineExpose({ onFocus, onBlur })
</script>

<style scoped>
.input_text_field {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

label {
  font-size: 16px;
  display: flex;
  transition: all ease 0.2s;
  left: 10px;
  pointer-events: none;
  margin: 0;
  color: rgba(17, 24, 39, 1);
  height: 26px;
  line-height: 22.4px;
}

.icon {
  position: absolute;
  right: 10px;
}

.box {
 position: absolute;
 right: 20px;
}

.red {
  background-color: #df5151;
}

.errorMessage {
  font-size: 14px;
  color: #df5151;
  margin-left: 8px;
}

input {
 height: 50px;
  background-color: var(--shade3);
  font-size: 16px;
  color: var(--shade6);
  width: 100%;
  box-sizing: border-box;
  font-family: Roboto;
  border: 1.5px solid var(--shade4) ;
  padding: 11.66px 19px 11.66px 19px;
  border-radius: 8px;
  border: 1.5px 0px 0px 0px;
  transition: all 0.3s ease;
}

input:hover {
  background-color: var(--shade2);
}

input:focus {
  background-color: var(--shade1);
  font-size: 16px;
  color: var(--black);
  font-weight: 600px;
  box-shadow: inset 0 1px 1px #00000013, 0 0 8px #66afe999;
  border: 1.5px 0px 0px 0px;
}

input:focus::placeholder {
  color: var(--shade5);

}

input::placeholder {
  color: var(--shade5);
}

input:disabled::placeholder {
  color: #000000;
}

input:disabled {
  background-color: #EAEAEA;
  color: #000000;
}

.strength_container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 4px;
  min-height: 2px;
  margin-top: 5px;
}

.strength {
  width: 100%;
  height: 2px;
  grid-column-gap: 5px;
}

.red {
  background-color: #df5151;
}

.orange {
  background-color: #df9851;
}

.green {
  background-color: #55af6d;
}

.blue {
  background-color: #517cdf;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none !important;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield !important;
  appearance: textfield !important;
}

.password_requirements {
  margin-top: 8px;
  padding: 12px;
  background-color: var(--shade2, #f8f9fa);
  border-radius: 8px;
}

.requirements_title {
  font-size: 14px;
  font-weight: 600;
  color: var(--shade6, #333);
  margin-bottom: 8px;
}

.requirements_list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.requirement_item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--shade5, #666);
  transition: all 0.2s ease;
}

.requirement_item.requirement_met {
  color: var(--success, #28a745);
}

.requirement_icon {
  flex-shrink: 0;
}
</style>
